import { logger } from '@/utils/logger';
import { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, Shield, Zap, Server, Database, Wifi, WifiOff } from 'lucide-react';
import { useConfig, useValidation, useServerInfo, useUpdateLatency, useUpdateFaults, useUpdateProxy, useUpdateProtocols, useUpdateAiMode, useUpdateValidation, useRestartServers, useRestartStatus } from '../hooks/useApi';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { toast } from 'sonner';
import {
  PageHeader,
  ModernCard,
  ModernBadge,
  Section
} from '../components/ui/DesignSystem';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from '../components/ui/Dialog';
import { EnvironmentManager } from '../components/workspace/EnvironmentManager';
import { AutocompleteInput } from '../components/ui/AutocompleteInput';
import { RealitySlider } from '../components/reality/RealitySlider';
import { RealityIndicator } from '../components/reality/RealityIndicator';
import { RealityPresetManager } from '../components/reality/RealityPresetManager';
import { useRealityShortcuts } from '../hooks/useRealityShortcuts';
import { authenticatedFetch } from '../utils/apiClient';
import { serverApi } from '../services/api';
import { useI18n } from '../i18n/I18nProvider';
import { isCloudMode } from '../utils/cloudMode';

function extractPort(address?: string): string {
  if (!address) return '';
  const parts = address.split(':');
  return parts[parts.length - 1] || '';
}

// Validation functions
function isValidUrl(url: string): boolean {
  if (!url) return true; // Empty URL is valid (optional field)
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidPort(port: number): boolean {
  return port >= 1 && port <= 65535;
}

export function ConfigPage() {
  const { t } = useI18n();
  const isCloud = isCloudMode();
  const [activeSection, setActiveSection] = useState<'general' | 'latency' | 'faults' | 'traffic-shaping' | 'proxy' | 'validation' | 'environment' | 'protocols' | 'reality'>(isCloud ? 'reality' : 'general');
  const { activeWorkspace } = useWorkspaceStore();
  const workspaceId = activeWorkspace?.id || 'default-workspace';
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showRestartDialog, setShowRestartDialog] = useState(false);
  const [hasPendingPortConfig, setHasPendingPortConfig] = useState(
    () => Boolean(localStorage.getItem('mockforge_pending_port_config'))
  );

  // Enable keyboard shortcuts for reality level changes
  useRealityShortcuts({
    onOpenPresetManager: () => {
      setActiveSection('reality');
      // Scroll to preset manager section
      setTimeout(() => {
        const presetSection = document.querySelector('[data-section="reality"]');
        if (presetSection) {
          presetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    },
  });

  // In cloud mode the local-server endpoints (/__mockforge/config, /validation,
  // /server-info) don't exist on the registry — these settings only apply to a
  // self-hosted MockForge instance. Skip the calls entirely and render only the
  // workspace-scoped sections (Reality, Environment).
  const { data: config, isLoading: configLoading } = useConfig({ enabled: !isCloud });
  const { data: validation, isLoading: validationLoading } = useValidation({ enabled: !isCloud });
  const { data: serverInfo, isLoading: serverInfoLoading } = useServerInfo({ enabled: !isCloud });

  // Mutations
  const updateLatency = useUpdateLatency();
  const updateFaults = useUpdateFaults();
  const updateProxy = useUpdateProxy();
  const updateProtocols = useUpdateProtocols();
  const updateAiMode = useUpdateAiMode();
  const updateValidation = useUpdateValidation();
  const restartServers = useRestartServers();
  const { data: restartStatus } = useRestartStatus();

  const [formData, setFormData] = useState({
    general: {
      http_port: 3000,
      ws_port: 3001,
      grpc_port: 50051,
      admin_port: 9080,
      ai_mode: 'live' as 'generate_once_freeze' | 'live'
    },
    restartInProgress: false,
    latency: { base_ms: 0, jitter_ms: 0, tag_overrides: {} as Record<string, number> },
    faults: { enabled: false, failure_rate: 0, status_codes: [] as number[] },
    trafficShaping: {
      enabled: false,
      bandwidth: {
        enabled: false,
        max_bytes_per_sec: 1048576, // 1 MB/s
        burst_capacity_bytes: 10485760 // 10 MB
      },
      burstLoss: {
        enabled: false,
        burst_probability: 0.1,
        burst_duration_ms: 5000,
        loss_rate_during_burst: 0.5,
        recovery_time_ms: 30000
      }
    },
    proxy: { enabled: false, upstream_url: '', timeout_seconds: 30 },
    validation: {
      mode: 'enforce' as 'enforce' | 'warn' | 'off',
      aggregate_errors: true,
      validate_responses: true,
      overrides: {} as Record<string, string>
    },
    protocols: {
      http: true,
      graphql: true,
      grpc: true,
      websocket: true,
      smtp: false,
      mqtt: false,
      ftp: false,
      kafka: false,
      rabbitmq: false,
      amqp: false,
      tcp: false
    },
    templateTest: ''
  });

  // Save port configuration to localStorage for persistence across restarts
  const savePortConfig = (ports: typeof formData.general) => {
    localStorage.setItem('mockforge_pending_port_config', JSON.stringify(ports));
  };

  // Load pending port configuration on mount
  useEffect(() => {
    const pendingConfig = localStorage.getItem('mockforge_pending_port_config');
    if (pendingConfig) {
      try {
        const ports = JSON.parse(pendingConfig);
        setFormData(prev => ({
          ...prev,
          general: { ...prev.general, ...ports }
        }));
        setHasPendingPortConfig(true);
      } catch (error) {
        logger.error('Failed to parse pending port config',error);
        localStorage.removeItem('mockforge_pending_port_config');
        setHasPendingPortConfig(false);
      }
    }
  }, []);

  // Monitor restart status
  useEffect(() => {
    if (restartStatus && formData.restartInProgress) {
      if (!restartStatus.restarting) {
        setFormData(prev => ({ ...prev, restartInProgress: false }));
        toast.success('Server restarted successfully! Port configuration applied.');
        localStorage.removeItem('mockforge_pending_port_config');
        setHasPendingPortConfig(false);
      }
    }
  }, [restartStatus, formData.restartInProgress]);

  // Initialize form data from API when data loads
  useEffect(() => {
    if (config?.latency) {
      setFormData(prev => ({
        ...prev,
        latency: {
          base_ms: config.latency.base_ms,
          jitter_ms: config.latency.jitter_ms,
          tag_overrides: config.latency.tag_overrides ?? {}
        }
      }));
    }
    if (config?.faults) {
      setFormData(prev => ({
        ...prev,
        faults: {
          enabled: config.faults.enabled,
          failure_rate: config.faults.failure_rate,
          status_codes: config.faults.status_codes
        }
      }));
    }
    if (config?.proxy) {
      setFormData(prev => ({
        ...prev,
        proxy: {
          enabled: config.proxy.enabled,
          upstream_url: config.proxy.upstream_url || '',
          timeout_seconds: config.proxy.timeout_seconds
        }
      }));
    }
    if (config?.protocols) {
      setFormData(prev => ({
        ...prev,
        protocols: { ...prev.protocols, ...config.protocols }
      }));
    }
    if (config?.traffic_shaping) {
      setFormData(prev => ({
        ...prev,
        trafficShaping: {
          enabled: config.traffic_shaping!.enabled,
          bandwidth: {
            enabled: config.traffic_shaping!.bandwidth.enabled,
            max_bytes_per_sec: config.traffic_shaping!.bandwidth.max_bytes_per_sec,
            burst_capacity_bytes: config.traffic_shaping!.bandwidth.burst_capacity_bytes
          },
          burstLoss: {
            enabled: config.traffic_shaping!.burst_loss.enabled,
            burst_probability: config.traffic_shaping!.burst_loss.burst_probability,
            burst_duration_ms: config.traffic_shaping!.burst_loss.burst_duration_ms,
            loss_rate_during_burst: config.traffic_shaping!.burst_loss.loss_rate_during_burst,
            recovery_time_ms: config.traffic_shaping!.burst_loss.recovery_time_ms
          }
        }
      }));
    }
    if (config?.ai_mode) {
      setFormData(prev => ({
        ...prev,
        general: { ...prev.general, ai_mode: config.ai_mode! }
      }));
    }
  }, [config]);

  useEffect(() => {
    if (serverInfo && !hasPendingPortConfig) {
      setFormData(prev => ({
        ...prev,
        general: {
          ...prev.general,
          http_port: parseInt(extractPort(serverInfo.http_server)) || 3000,
          ws_port: parseInt(extractPort(serverInfo.ws_server)) || 3001,
          grpc_port: parseInt(extractPort(serverInfo.grpc_server)) || 50051,
          admin_port: serverInfo.admin_port || 9080
        }
      }));
    }
  }, [serverInfo, hasPendingPortConfig]);

  useEffect(() => {
    if (validation) {
      setFormData(prev => ({
        ...prev,
        validation: {
          mode: validation.mode as 'enforce' | 'warn' | 'off',
          aggregate_errors: validation.aggregate_errors,
          validate_responses: validation.validate_responses,
          overrides: validation.overrides
        }
      }));
    }
  }, [validation]);

  // Detect changes by comparing current form data to server data
  useEffect(() => {
    let hasChanges = false;

    // Check general settings
    if (serverInfo) {
      const currentHttpPort = parseInt(extractPort(serverInfo.http_server)) || 3000;
      const currentWsPort = parseInt(extractPort(serverInfo.ws_server)) || 3001;
      const currentGrpcPort = parseInt(extractPort(serverInfo.grpc_server)) || 50051;
      const currentAdminPort = serverInfo.admin_port || 9080;

      if (formData.general.http_port !== currentHttpPort ||
          formData.general.ws_port !== currentWsPort ||
          formData.general.grpc_port !== currentGrpcPort ||
          formData.general.admin_port !== currentAdminPort) {
        hasChanges = true;
      }
    }
    if ((config?.ai_mode ?? 'live') !== formData.general.ai_mode) {
      hasChanges = true;
    }

    // Check latency settings
    if (config?.latency) {
      if (formData.latency.base_ms !== config.latency.base_ms ||
          formData.latency.jitter_ms !== config.latency.jitter_ms ||
          JSON.stringify(formData.latency.tag_overrides) !== JSON.stringify(config.latency.tag_overrides ?? {})) {
        hasChanges = true;
      }
    }

    // Check fault settings
    if (config?.faults) {
      if (formData.faults.enabled !== config.faults.enabled ||
          formData.faults.failure_rate !== config.faults.failure_rate ||
          JSON.stringify(formData.faults.status_codes) !== JSON.stringify(config.faults.status_codes)) {
        hasChanges = true;
      }
    }

    // Check proxy settings
    if (config?.proxy) {
      if (formData.proxy.enabled !== config.proxy.enabled ||
          formData.proxy.upstream_url !== (config.proxy.upstream_url || '') ||
          formData.proxy.timeout_seconds !== config.proxy.timeout_seconds) {
        hasChanges = true;
      }
    }

    // Check validation settings
    if (validation) {
      if (formData.validation.mode !== validation.mode ||
          formData.validation.aggregate_errors !== validation.aggregate_errors ||
          formData.validation.validate_responses !== validation.validate_responses ||
          JSON.stringify(formData.validation.overrides) !== JSON.stringify(validation.overrides)) {
        hasChanges = true;
      }
    }

    setHasUnsavedChanges(hasChanges);
  }, [formData, config, validation, serverInfo]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSave = async (section: string) => {
    // Validate before saving
    if (section === 'proxy' && formData.proxy.enabled) {
      if (!formData.proxy.upstream_url) {
        toast.error('Upstream URL is required when proxy is enabled');
        return;
      }
      if (!isValidUrl(formData.proxy.upstream_url)) {
        toast.error('Invalid upstream URL. Must be a valid HTTP or HTTPS URL');
        return;
      }
      if (!isValidPort(formData.proxy.timeout_seconds)) {
        toast.error('Invalid timeout. Must be between 1 and 300 seconds');
        return;
      }
    }

    if (section === 'general') {
      if (!isValidPort(formData.general.http_port)) {
        toast.error('Invalid HTTP port. Must be between 1 and 65535');
        return;
      }
      if (!isValidPort(formData.general.ws_port)) {
        toast.error('Invalid WebSocket port. Must be between 1 and 65535');
        return;
      }
      if (!isValidPort(formData.general.grpc_port)) {
        toast.error('Invalid gRPC port. Must be between 1 and 65535');
        return;
      }
      if (!isValidPort(formData.general.admin_port)) {
        toast.error('Invalid Admin port. Must be between 1 and 65535');
        return;
      }
    }

    try {
      switch (section) {
        case 'latency':
          await updateLatency.mutateAsync({
            name: 'default',
            base_ms: formData.latency.base_ms,
            jitter_ms: formData.latency.jitter_ms,
            tag_overrides: formData.latency.tag_overrides
          });
          toast.success('Latency configuration saved successfully');
          break;

        case 'faults':
          await updateFaults.mutateAsync({
            enabled: formData.faults.enabled,
            failure_rate: formData.faults.failure_rate,
            status_codes: formData.faults.status_codes,
            active_failures: 0
          });
          toast.success('Fault injection configuration saved successfully');
          break;

        case 'proxy':
          await updateProxy.mutateAsync({
            enabled: formData.proxy.enabled,
            upstream_url: formData.proxy.upstream_url,
            timeout_seconds: formData.proxy.timeout_seconds,
            requests_proxied: 0
          });
          toast.success('Proxy configuration saved successfully');
          break;

        case 'validation':
          await updateValidation.mutateAsync({
            mode: formData.validation.mode,
            aggregate_errors: formData.validation.aggregate_errors,
            validate_responses: formData.validation.validate_responses,
            overrides: formData.validation.overrides
          });
          toast.success('Validation settings saved successfully');
          break;

        case 'general': {
          // If ports changed, prompt the restart flow; otherwise persist ai_mode now.
          const currentHttpPort = parseInt(extractPort(serverInfo?.http_server)) || 3000;
          const currentWsPort = parseInt(extractPort(serverInfo?.ws_server)) || 3001;
          const currentGrpcPort = parseInt(extractPort(serverInfo?.grpc_server)) || 50051;
          const currentAdminPort = serverInfo?.admin_port || 9080;
          const portsChanged =
            formData.general.http_port !== currentHttpPort ||
            formData.general.ws_port !== currentWsPort ||
            formData.general.grpc_port !== currentGrpcPort ||
            formData.general.admin_port !== currentAdminPort;

          if (portsChanged) {
            // Open the restart dialog synchronously; ai_mode is persisted as part of confirm.
            setShowRestartDialog(true);
          } else {
            await updateAiMode.mutateAsync(formData.general.ai_mode);
            toast.success('General settings saved successfully');
          }
          break;
        }

        case 'protocols':
          await updateProtocols.mutateAsync(formData.protocols);
          toast.success('Protocol configuration saved successfully');
          break;

        case 'traffic-shaping':
          // Traffic shaping configuration
          try {
            const response = await authenticatedFetch('/__mockforge/config/traffic-shaping', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(formData.trafficShaping)
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast.success('Traffic shaping configuration saved successfully');
          } catch (error) {
            logger.error('Error saving traffic shaping',error);
            toast.error('Failed to save traffic shaping configuration');
          }
          break;

        default:
          toast.error(`Unknown section: ${section}`);
      }
    } catch (error) {
      logger.error(`Error saving ${section} configuration:`, error);
      toast.error(`Failed to save ${section} configuration`);
    }
  };

  const handleConfirmRestart = async () => {
    setShowRestartDialog(false);
    try {
      setFormData(prev => ({ ...prev, restartInProgress: true }));
      toast.info('Saving configuration and restarting server...');

      // Persist ai_mode before restart so the new process picks it up.
      await updateAiMode.mutateAsync(formData.general.ai_mode);

      await restartServers.mutateAsync('Port configuration updated');

      // Poll server-info up to 5 times, 500ms apart, to confirm the expected
      // ports are actually listening before persisting anything.
      const expected = formData.general;
      let verified = false;
      for (let attempt = 0; attempt < 5; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        try {
          const info = await serverApi.getServerInfo();
          const httpPort = extractPort(info.http_server);
          const wsPort = extractPort(info.ws_server);
          const grpcPort = extractPort(info.grpc_server);
          const httpOk = !info.http_server || httpPort === String(expected.http_port);
          const wsOk = !info.ws_server || wsPort === String(expected.ws_port);
          const grpcOk = !info.grpc_server || grpcPort === String(expected.grpc_port);
          if (httpOk && wsOk && grpcOk) {
            verified = true;
            break;
          }
        } catch (pollErr) {
          logger.error('server-info poll failed', pollErr);
        }
      }

      setFormData(prev => ({ ...prev, restartInProgress: false }));
      if (verified) {
        savePortConfig(expected);
        setHasPendingPortConfig(true);
        toast.success('Server restarted successfully! Port configuration applied.');
      } else {
        localStorage.removeItem('mockforge_pending_port_config');
        setHasPendingPortConfig(false);
        toast.error('Server restarted but ports could not be verified — please reload manually');
      }
    } catch (error) {
      setFormData(prev => ({ ...prev, restartInProgress: false }));
      toast.error('Failed to restart server. Please restart manually.');
      logger.error('Server restart failed',error);
    }
  };

  const handleCancelRestart = () => {
    setShowRestartDialog(false);
    toast.info('Configuration saved locally. Restart the server manually to apply changes.');
  };

  const handleReset = (section: string) => {
    switch (section) {
      case 'general':
        if (serverInfo) {
          setFormData(prev => ({
            ...prev,
            general: {
              http_port: parseInt(extractPort(serverInfo.http_server)) || 3000,
              ws_port: parseInt(extractPort(serverInfo.ws_server)) || 3001,
              grpc_port: parseInt(extractPort(serverInfo.grpc_server)) || 50051,
              admin_port: serverInfo.admin_port || 9080,
              ai_mode: (config?.ai_mode ?? 'live') as 'live' | 'generate_once_freeze'
            }
          }));
          toast.info('General settings reset to server values');
        }
        break;

      case 'latency':
        if (config?.latency) {
          setFormData(prev => ({
            ...prev,
            latency: {
              base_ms: config.latency.base_ms,
              jitter_ms: config.latency.jitter_ms,
              tag_overrides: config.latency.tag_overrides ?? {}
            }
          }));
          toast.info('Latency configuration reset to server values');
        }
        break;

      case 'faults':
        if (config?.faults) {
          setFormData(prev => ({
            ...prev,
            faults: {
              enabled: config.faults.enabled,
              failure_rate: config.faults.failure_rate,
              status_codes: config.faults.status_codes
            }
          }));
          toast.info('Fault injection configuration reset to server values');
        }
        break;

      case 'proxy':
        if (config?.proxy) {
          setFormData(prev => ({
            ...prev,
            proxy: {
              enabled: config.proxy.enabled,
              upstream_url: config.proxy.upstream_url || '',
              timeout_seconds: config.proxy.timeout_seconds
            }
          }));
          toast.info('Proxy configuration reset to server values');
        }
        break;

      case 'validation':
        if (validation) {
          setFormData(prev => ({
            ...prev,
            validation: {
              mode: validation.mode as 'enforce' | 'warn' | 'off',
              aggregate_errors: validation.aggregate_errors,
              validate_responses: validation.validate_responses,
              overrides: validation.overrides
            }
          }));
          toast.info('Validation settings reset to server values');
        }
        break;

      case 'traffic-shaping':
        setFormData(prev => ({
          ...prev,
          trafficShaping: {
            enabled: false,
            bandwidth: {
              enabled: false,
              max_bytes_per_sec: 1048576,
              burst_capacity_bytes: 10485760
            },
            burstLoss: {
              enabled: false,
              burst_probability: 0.1,
              burst_duration_ms: 5000,
              loss_rate_during_burst: 0.5,
              recovery_time_ms: 30000
            }
          }
        }));
        toast.info('Traffic shaping configuration reset to defaults');
        break;

      default:
        toast.error(`Unknown section: ${section}`);
    }
  };

  const handleResetAll = () => {
    handleReset('general');
    handleReset('latency');
    handleReset('faults');
    handleReset('traffic-shaping');
    handleReset('proxy');
    handleReset('validation');
    toast.success('All settings reset to server values');
  };

  const handleSaveAll = async () => {
    const sections = ['general', 'latency', 'faults', 'traffic-shaping', 'proxy', 'validation', 'protocols'];
    let successCount = 0;
    let errorCount = 0;

    for (const section of sections) {
      try {
        await handleSave(section);
        successCount++;
      } catch (_error) {
        errorCount++;
      }
    }

    if (errorCount === 0) {
      toast.success('All settings saved successfully');
    } else {
      toast.warning(`Saved ${successCount} sections, ${errorCount} failed`);
    }
  };

  if (configLoading || validationLoading || serverInfoLoading) {
    return (
      <div className="space-y-8">
        <PageHeader
          title={t('page.config.title')}
          subtitle={t('page.config.subtitle')}
        />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-info-600"></div>
          <span className="ml-3 text-lg text-muted-foreground">Loading configuration...</span>
        </div>
      </div>
    );
  }

  const allSections = [
    { id: 'reality', label: 'Reality Slider', icon: Zap, description: 'Unified realism control', cloudAvailable: true },
    { id: 'general', label: 'General', icon: Settings, description: 'Basic MockForge settings', cloudAvailable: false },
    { id: 'protocols', label: 'Protocols', icon: Server, description: 'Protocol enable/disable settings', cloudAvailable: false },
    { id: 'latency', label: 'Latency', icon: Zap, description: 'Response delay and timing', cloudAvailable: false },
    { id: 'faults', label: 'Fault Injection', icon: Shield, description: 'Error simulation and failure modes', cloudAvailable: false },
    { id: 'traffic-shaping', label: 'Traffic Shaping', icon: Wifi, description: 'Bandwidth control and network simulation', cloudAvailable: false },
    { id: 'proxy', label: 'Proxy', icon: Server, description: 'Upstream proxy configuration', cloudAvailable: false },
    { id: 'validation', label: 'Validation', icon: Database, description: 'Request/response validation', cloudAvailable: false },
    { id: 'environment', label: 'Environment', icon: Settings, description: 'Environment variables', cloudAvailable: true },
  ];
  const sections = isCloud ? allSections.filter(s => s.cloudAvailable) : allSections;

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('page.config.title')}
        subtitle={
          hasUnsavedChanges
            ? "⚠️ You have unsaved changes"
            : t('page.config.subtitle')
        }
        action={
          <div className="flex items-center gap-3">
            <RealityIndicator />
            {!isCloud && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={handleResetAll}
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset All
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={handleSaveAll}
                >
                  <Save className="h-4 w-4" />
                  Save All Changes
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Navigation Sidebar */}
        <div className="lg:col-span-1">
          <ModernCard>
            <nav className="space-y-2">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id as typeof activeSection)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                      activeSection === section.id
                        ? 'bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300'
                        : 'hover:bg-accent hover:text-accent-foreground/50 text-foreground'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <div>
                      <div className="font-medium">{section.label}</div>
                      <div className="text-xs opacity-75">{section.description}</div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </ModernCard>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {activeSection === 'reality' && (
            <Section title="Reality Slider" subtitle="Unified control for chaos, latency, and MockAI" data-section="reality">
              <div className="space-y-6">
                <RealitySlider />
                <RealityPresetManager />
                <div className="mt-4 p-4 rounded-lg bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800">
                  <h4 className="text-sm font-semibold text-info-900 dark:text-info-100 mb-2">
                    Keyboard Shortcuts
                  </h4>
                  <div className="text-xs text-info-700 dark:text-info-200 space-y-1">
                    <div><kbd className="px-1.5 py-0.5 bg-card rounded border border-info-300 dark:border-info-700">Ctrl+Shift+1-5</kbd> Set reality level</div>
                    <div><kbd className="px-1.5 py-0.5 bg-card rounded border border-info-300 dark:border-info-700">Ctrl+Shift+R</kbd> Reset to default (Level 3)</div>
                    <div><kbd className="px-1.5 py-0.5 bg-card rounded border border-info-300 dark:border-info-700">Ctrl+Shift+P</kbd> Open preset manager</div>
                  </div>
                </div>
              </div>
            </Section>
          )}

          {activeSection === 'general' && (
            <Section title="General Settings" subtitle="Basic MockForge configuration">
              <ModernCard>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Server Configuration
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">
                          HTTP Port
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="65535"
                          value={formData.general.http_port}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            general: { ...prev.general, http_port: parseInt(e.target.value) || 3000 }
                          }))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">
                          WebSocket Port
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="65535"
                          value={formData.general.ws_port}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            general: { ...prev.general, ws_port: parseInt(e.target.value) || 3001 }
                          }))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">
                          gRPC Port
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="65535"
                          value={formData.general.grpc_port}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            general: { ...prev.general, grpc_port: parseInt(e.target.value) || 50051 }
                          }))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">
                          Admin Port
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="65535"
                          value={formData.general.admin_port}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            general: { ...prev.general, admin_port: parseInt(e.target.value) || 9080 }
                          }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* AI Mode Configuration */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      AI Mode
                    </label>
                    <div className="space-y-4">
                      <div className="p-4 bg-muted rounded-lg border border-border">
                        <p className="text-sm text-muted-foreground mb-4">
                          Control how AI-generated artifacts are used at runtime. In <strong>Generate Once Freeze</strong> mode,
                          AI is only used to produce config/templates, and runtime mocks use frozen artifacts (no LLM calls).
                          In <strong>Live</strong> mode, AI is used dynamically at runtime for each request.
                        </p>
                        <Select
                          value={formData.general.ai_mode || 'live'}
                          onValueChange={(value) => {
                            setFormData(prev => ({
                              ...prev,
                              general: { ...prev.general, ai_mode: value as 'generate_once_freeze' | 'live' }
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="live">Live - AI used dynamically at runtime</SelectItem>
                            <SelectItem value="generate_once_freeze">Generate Once Freeze - Use frozen artifacts only</SelectItem>
                          </SelectContent>
                        </Select>
                        {formData.general.ai_mode === 'generate_once_freeze' && (
                          <div className="mt-3 p-3 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded">
                            <p className="text-xs text-info-700 dark:text-info-200">
                              <strong>Note:</strong> In this mode, AI-generated scenarios and personas will use frozen artifacts.
                              Make sure to freeze your AI-generated artifacts before using them in this mode.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {formData.restartInProgress && (
                    <div className="flex items-center gap-2 p-3 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg mb-4">
                      <RefreshCw className="w-4 h-4 animate-spin text-info-600" />
                      <span className="text-sm text-info-700 dark:text-info-300">
                        Server restart in progress... Configuration will be applied shortly.
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <Button variant="outline" onClick={() => handleReset('general')} disabled={formData.restartInProgress}>
                      Reset
                    </Button>
                    <Button onClick={() => handleSave('general')} disabled={formData.restartInProgress}>
                      {formData.restartInProgress ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Restarting...
                        </>
                      ) : (
                        'Save & Restart Server'
                      )}
                    </Button>
                  </div>
                </div>
              </ModernCard>
            </Section>
           )}

           {activeSection === 'protocols' && (
             <Section title="Protocol Configuration" subtitle="Enable or disable protocol support">
               <ModernCard>
                 <div className="space-y-6">
                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                     {[
                       { key: 'http', label: 'HTTP/REST', description: 'RESTful API mocking' },
                       { key: 'graphql', label: 'GraphQL', description: 'GraphQL API mocking' },
                       { key: 'grpc', label: 'gRPC', description: 'gRPC service mocking' },
                       { key: 'websocket', label: 'WebSocket', description: 'WebSocket connection mocking' },
                       { key: 'smtp', label: 'SMTP', description: 'Email protocol mocking' },
                       { key: 'mqtt', label: 'MQTT', description: 'IoT messaging protocol' },
                       { key: 'ftp', label: 'FTP', description: 'File transfer protocol' },
                       { key: 'kafka', label: 'Kafka', description: 'Event streaming platform' },
                       { key: 'rabbitmq', label: 'RabbitMQ', description: 'Message queuing system' },
                       { key: 'amqp', label: 'AMQP', description: 'Advanced message queuing' },
                       { key: 'tcp', label: 'TCP', description: 'Raw TCP socket mocking' },
                     ].map((protocol) => (
                       <div key={protocol.key} className="flex items-center justify-between p-4 border border-border rounded-lg">
                         <div>
                           <div className="font-medium text-foreground">{protocol.label}</div>
                           <div className="text-sm text-muted-foreground">{protocol.description}</div>
                         </div>
                         <label className="relative inline-flex items-center cursor-pointer">
                           <input
                             type="checkbox"
                             className="sr-only peer"
                             checked={formData.protocols?.[protocol.key as keyof typeof formData.protocols] ?? false}
                             onChange={(e) => setFormData(prev => ({
                               ...prev,
                               protocols: {
                                 ...prev.protocols,
                                 [protocol.key]: e.target.checked
                               }
                             }))}
                           />
                           <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/30 dark:peer-focus:ring-ring/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                         </label>
                       </div>
                     ))}
                   </div>
                 </div>
               </ModernCard>
             </Section>
           )}

           {activeSection === 'latency' && (
            <Section title="Latency Configuration" subtitle="Control response timing and delays">
              <ModernCard>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Base Latency (ms)
                      </label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={formData.latency.base_ms}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          latency: { ...prev.latency, base_ms: parseInt(e.target.value) || 0 }
                        }))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Minimum response time for all requests
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Jitter (ms)
                      </label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={formData.latency.jitter_ms}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          latency: { ...prev.latency, jitter_ms: parseInt(e.target.value) || 0 }
                        }))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Random delay variation (± jitter)
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <h3 className="text-sm font-medium text-foreground mb-1">
                      Per-Tag Overrides
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3">
                      Override the base latency for specific route tags. Uses the global base_ms unless a matching tag is found.
                    </p>
                    <div className="space-y-2">
                      {Object.entries(formData.latency.tag_overrides).map(([tag, ms]) => (
                        <div key={tag} className="flex items-center gap-2">
                          <Input
                            type="text"
                            value={tag}
                            placeholder="users"
                            className="flex-1"
                            onChange={(e) => {
                              const newTag = e.target.value;
                              setFormData(prev => {
                                const next = { ...prev.latency.tag_overrides };
                                delete next[tag];
                                if (newTag.length > 0) next[newTag] = ms;
                                return { ...prev, latency: { ...prev.latency, tag_overrides: next } };
                              });
                            }}
                          />
                          <Input
                            type="number"
                            min="0"
                            value={ms}
                            placeholder="ms"
                            className="w-32"
                            onChange={(e) => {
                              const v = parseInt(e.target.value) || 0;
                              setFormData(prev => ({
                                ...prev,
                                latency: {
                                  ...prev.latency,
                                  tag_overrides: { ...prev.latency.tag_overrides, [tag]: v }
                                }
                              }));
                            }}
                          />
                          <span className="text-xs text-muted-foreground">ms</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFormData(prev => {
                                const next = { ...prev.latency.tag_overrides };
                                delete next[tag];
                                return { ...prev, latency: { ...prev.latency, tag_overrides: next } };
                              });
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFormData(prev => {
                            let i = 1;
                            let tag = 'tag';
                            while (tag in prev.latency.tag_overrides) {
                              i += 1;
                              tag = `tag${i}`;
                            }
                            return {
                              ...prev,
                              latency: {
                                ...prev.latency,
                                tag_overrides: { ...prev.latency.tag_overrides, [tag]: 100 }
                              }
                            };
                          });
                        }}
                      >
                        Add tag override
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <Button variant="outline" onClick={() => handleReset('latency')}>
                      Reset
                    </Button>
                    <Button onClick={() => handleSave('latency')}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              </ModernCard>
            </Section>
          )}

          {activeSection === 'faults' && (
            <Section title="Fault Injection" subtitle="Configure error simulation and failure scenarios">
              <ModernCard>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-foreground">
                        Enable Fault Injection
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Simulate network failures and server errors
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.faults.enabled}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          faults: { ...prev.faults, enabled: e.target.checked }
                        }))}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/30 dark:peer-focus:ring-ring/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {formData.faults.enabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Failure Rate (%)
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="5"
                          value={formData.faults.failure_rate}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            faults: { ...prev.faults, failure_rate: parseInt(e.target.value) || 0 }
                          }))}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Error Status Codes
                        </label>
                      <div className="flex flex-wrap gap-2">
                        {[500, 502, 503, 504, 400, 401, 403, 404].map(code => (
                          <button
                            key={code}
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                faults: {
                                  ...prev.faults,
                                  status_codes: prev.faults.status_codes.includes(code)
                                    ? prev.faults.status_codes.filter(c => c !== code)
                                    : [...prev.faults.status_codes, code]
                                }
                              }));
                            }}
                            className="cursor-pointer"
                          >
                            <ModernBadge
                              variant={formData.faults.status_codes.includes(code) ? 'error' : 'outline'}
                            >
                              {code}
                            </ModernBadge>
                          </button>
                        ))}
                      </div>
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <Button variant="outline" onClick={() => handleReset('faults')}>
                      Reset
                    </Button>
                    <Button onClick={() => handleSave('faults')}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              </ModernCard>
            </Section>
          )}

          {activeSection === 'traffic-shaping' && (
            <Section title="Traffic Shaping" subtitle="Control bandwidth and simulate network conditions">
              <ModernCard>
                <div className="space-y-8">
                  {/* Overall Traffic Shaping Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-foreground">
                        Enable Traffic Shaping
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Simulate real network conditions with bandwidth control and connectivity issues
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.trafficShaping.enabled}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          trafficShaping: { ...prev.trafficShaping, enabled: e.target.checked }
                        }))}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/30 dark:peer-focus:ring-ring/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {formData.trafficShaping.enabled && (
                    <>
                      {/* Bandwidth Control Section */}
                      <div className="border-t border-border pt-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Wifi className="h-5 w-5 text-info-600" />
                          <h3 className="text-lg font-medium text-foreground">
                            Bandwidth Control
                          </h3>
                        </div>

                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-sm font-medium text-foreground">
                              Enable Bandwidth Throttling
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Limit data transfer rates using token bucket algorithm
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={formData.trafficShaping.bandwidth.enabled}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                trafficShaping: {
                                  ...prev.trafficShaping,
                                  bandwidth: { ...prev.trafficShaping.bandwidth, enabled: e.target.checked }
                                }
                              }))}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/30 dark:peer-focus:ring-ring/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                          </label>
                        </div>

                        {formData.trafficShaping.bandwidth.enabled && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Max Bandwidth (bytes/sec)
                              </label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="1048576"
                                value={formData.trafficShaping.bandwidth.max_bytes_per_sec}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  trafficShaping: {
                                    ...prev.trafficShaping,
                                    bandwidth: {
                                      ...prev.trafficShaping.bandwidth,
                                      max_bytes_per_sec: parseInt(e.target.value) || 1048576
                                    }
                                  }
                                }))}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Maximum data transfer rate (1 MB/s = 1,048,576 bytes)
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Burst Capacity (bytes)
                              </label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="10485760"
                                value={formData.trafficShaping.bandwidth.burst_capacity_bytes}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  trafficShaping: {
                                    ...prev.trafficShaping,
                                    bandwidth: {
                                      ...prev.trafficShaping.bandwidth,
                                      burst_capacity_bytes: parseInt(e.target.value) || 10485760
                                    }
                                  }
                                }))}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Token bucket capacity for burst traffic (10 MB = 10,485,760 bytes)
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Burst Loss Section */}
                      <div className="border-t border-border pt-6">
                        <div className="flex items-center gap-3 mb-4">
                          <WifiOff className="h-5 w-5 text-orange-600" />
                          <h3 className="text-lg font-medium text-foreground">
                            Burst Loss Simulation
                          </h3>
                        </div>

                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-sm font-medium text-foreground">
                              Enable Burst Loss
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Simulate intermittent connectivity issues and packet loss
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={formData.trafficShaping.burstLoss.enabled}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                trafficShaping: {
                                  ...prev.trafficShaping,
                                  burstLoss: { ...prev.trafficShaping.burstLoss, enabled: e.target.checked }
                                }
                              }))}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/30 dark:peer-focus:ring-ring/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                          </label>
                        </div>

                        {formData.trafficShaping.burstLoss.enabled && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Burst Probability (%)
                              </label>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                placeholder="10"
                                value={formData.trafficShaping.burstLoss.burst_probability * 100}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  trafficShaping: {
                                    ...prev.trafficShaping,
                                    burstLoss: {
                                      ...prev.trafficShaping.burstLoss,
                                      burst_probability: parseFloat(e.target.value) / 100 || 0.1
                                    }
                                  }
                                }))}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Probability of entering a loss burst (0-100%)
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Burst Duration (ms)
                              </label>
                              <Input
                                type="number"
                                min="100"
                                placeholder="5000"
                                value={formData.trafficShaping.burstLoss.burst_duration_ms}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  trafficShaping: {
                                    ...prev.trafficShaping,
                                    burstLoss: {
                                      ...prev.trafficShaping.burstLoss,
                                      burst_duration_ms: parseInt(e.target.value) || 5000
                                    }
                                  }
                                }))}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Duration of loss bursts in milliseconds
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Loss Rate During Burst (%)
                              </label>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                placeholder="50"
                                value={formData.trafficShaping.burstLoss.loss_rate_during_burst * 100}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  trafficShaping: {
                                    ...prev.trafficShaping,
                                    burstLoss: {
                                      ...prev.trafficShaping.burstLoss,
                                      loss_rate_during_burst: parseFloat(e.target.value) / 100 || 0.5
                                    }
                                  }
                                }))}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Packet loss rate during burst periods (0-100%)
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Recovery Time (ms)
                              </label>
                              <Input
                                type="number"
                                min="1000"
                                placeholder="30000"
                                value={formData.trafficShaping.burstLoss.recovery_time_ms}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  trafficShaping: {
                                    ...prev.trafficShaping,
                                    burstLoss: {
                                      ...prev.trafficShaping.burstLoss,
                                      recovery_time_ms: parseInt(e.target.value) || 30000
                                    }
                                  }
                                }))}
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Recovery period between bursts in milliseconds
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <Button variant="outline" onClick={() => handleReset('traffic-shaping')}>
                      Reset
                    </Button>
                    <Button onClick={() => handleSave('traffic-shaping')}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              </ModernCard>
            </Section>
          )}

          {activeSection === 'proxy' && (
            <Section title="Proxy Configuration" subtitle="Configure upstream proxy settings">
              <ModernCard>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-foreground">
                        Enable Proxy Mode
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Forward requests to upstream services
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.proxy.enabled}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          proxy: { ...prev.proxy, enabled: e.target.checked }
                        }))}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring/30 dark:peer-focus:ring-ring/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {formData.proxy.enabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Upstream URL
                        </label>
                        <Input
                          type="url"
                          placeholder="https://api.example.com"
                          value={formData.proxy.upstream_url}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            proxy: { ...prev.proxy, upstream_url: e.target.value }
                          }))}
                          className={
                            formData.proxy.upstream_url && !isValidUrl(formData.proxy.upstream_url)
                              ? 'border-destructive dark:border-destructive'
                              : ''
                          }
                        />
                        {formData.proxy.upstream_url && !isValidUrl(formData.proxy.upstream_url) && (
                          <p className="text-xs text-danger-600 dark:text-danger-400 mt-1">
                            Must be a valid HTTP or HTTPS URL
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Timeout (seconds)
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="300"
                          placeholder="30"
                          value={formData.proxy.timeout_seconds}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            proxy: { ...prev.proxy, timeout_seconds: parseInt(e.target.value) || 30 }
                          }))}
                        />
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <Button variant="outline" onClick={() => handleReset('proxy')}>
                      Reset
                    </Button>
                    <Button onClick={() => handleSave('proxy')}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              </ModernCard>
            </Section>
          )}

          {activeSection === 'validation' && (
            <Section title="Validation Settings" subtitle="Configure request and response validation">
              <ModernCard>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Validation Mode
                    </label>
                    <select
                      value={formData.validation.mode}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        validation: { ...prev.validation, mode: e.target.value as 'enforce' | 'warn' | 'off' }
                      }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
                    >
                      <option value="enforce">Enforce (Strict)</option>
                      <option value="warn">Warn Only</option>
                      <option value="off">Disabled</option>
                    </select>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-foreground">
                          Aggregate Errors
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Collect all validation errors before responding
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.validation.aggregate_errors}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          validation: { ...prev.validation, aggregate_errors: e.target.checked }
                        }))}
                        className="w-4 h-4 text-info-600 bg-muted border-border rounded focus:ring-ring/30"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-foreground">
                          Validate Responses
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Check response format and content
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.validation.validate_responses}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          validation: { ...prev.validation, validate_responses: e.target.checked }
                        }))}
                        className="w-4 h-4 text-info-600 bg-muted border-border rounded focus:ring-ring/30"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <h3 className="text-sm font-medium text-foreground mb-1">
                      Per-Route Overrides
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3">
                      Override the validation mode for specific routes (e.g. <code>GET /users/{`{id}`}</code>). Use the global mode by default; add an entry only when a route should differ.
                    </p>
                    <div className="space-y-2">
                      {Object.entries(formData.validation.overrides).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <Input
                            type="text"
                            value={key}
                            placeholder="GET /users/{id}"
                            className="flex-1"
                            onChange={(e) => {
                              const newKey = e.target.value;
                              setFormData(prev => {
                                const next = { ...prev.validation.overrides };
                                delete next[key];
                                if (newKey.length > 0) next[newKey] = value;
                                return { ...prev, validation: { ...prev.validation, overrides: next } };
                              });
                            }}
                          />
                          <select
                            value={value}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                validation: {
                                  ...prev.validation,
                                  overrides: { ...prev.validation.overrides, [key]: newValue }
                                }
                              }));
                            }}
                            className="px-3 py-2 border border-border rounded-lg bg-card text-foreground"
                          >
                            <option value="enforce">enforce</option>
                            <option value="warn">warn</option>
                            <option value="off">off</option>
                          </select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFormData(prev => {
                                const next = { ...prev.validation.overrides };
                                delete next[key];
                                return { ...prev, validation: { ...prev.validation, overrides: next } };
                              });
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFormData(prev => {
                            // Pick a placeholder key that isn't already taken
                            let i = 1;
                            let key = 'GET /path';
                            while (key in prev.validation.overrides) {
                              i += 1;
                              key = `GET /path${i}`;
                            }
                            return {
                              ...prev,
                              validation: {
                                ...prev.validation,
                                overrides: { ...prev.validation.overrides, [key]: 'warn' }
                              }
                            };
                          });
                        }}
                      >
                        Add override
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <Button variant="outline" onClick={() => handleReset('validation')}>
                      Reset
                    </Button>
                    <Button onClick={() => handleSave('validation')}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              </ModernCard>
            </Section>
          )}

          {activeSection === 'environment' && (
            <Section title="Environments & Variables" subtitle="Manage environments and their variables">
              <EnvironmentManager
                workspaceId={workspaceId}
                onEnvironmentSelect={(_envId) => {
                  // Could update URL or notify other components
                }}
              />

              {/* Template Testing Section */}
              <div className="mt-8">
                <ModernCard>
                  <h3 className="text-lg font-medium text-foreground mb-4">
                    Template Testing
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Test variable substitution in templates. Type {'{{'} to see available variables.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Template Input (with autocomplete)
                      </label>
                      <AutocompleteInput
                        value={formData.templateTest || ''}
                        onChange={(value) => setFormData(prev => ({ ...prev, templateTest: value }))}
                        placeholder="Type {{ to see available variables..."
                        workspaceId={workspaceId}
                        context="template_test"
                        className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Expected Output
                      </label>
                      <div className="p-3 bg-muted rounded-lg font-mono text-sm text-muted-foreground">
                        {formData.templateTest || 'Template output will appear here...'}
                      </div>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      💡 Tip: Use Ctrl+Space anywhere in a text input to manually trigger autocomplete
                    </div>
                  </div>
                </ModernCard>
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* Restart Confirmation Dialog */}
      <Dialog open={showRestartDialog} onOpenChange={setShowRestartDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restart Server Required</DialogTitle>
            <DialogClose onClick={handleCancelRestart} />
          </DialogHeader>
          <DialogDescription>
            Port configuration changes require a server restart to take effect.
          </DialogDescription>
          <div className="py-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-medium">HTTP Port:</span>
                <span>{formData.general.http_port}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">WebSocket Port:</span>
                <span>{formData.general.ws_port}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">gRPC Port:</span>
                <span>{formData.general.grpc_port}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Admin Port:</span>
                <span>{formData.general.admin_port}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelRestart}>
              Cancel
            </Button>
            <Button onClick={handleConfirmRestart}>
              Restart Server
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
